import { forwardRef, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
// ApexCharts types are not re-exported from the package; use permissive aliases
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApexAxisChartSeries = Record<string, any>[];
type ApexNonAxisChartSeries = number[] | ApexAxisChartSeries;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApexOptions = Record<string, any>;

export type ChartType =
  | 'line'
  | 'area'
  | 'bar'
  | 'pie'
  | 'donut'
  | 'radialBar'
  | 'scatter'
  | 'bubble'
  | 'heatmap'
  | 'candlestick'
  | 'boxPlot'
  | 'radar'
  | 'polarArea'
  | 'rangeBar'
  | 'rangeArea'
  | 'treemap';

export type ChartSeries = ApexAxisChartSeries | ApexNonAxisChartSeries;

export type ChartProps = {
  type: ChartType;
  series: ChartSeries;
  options?: ApexOptions;
  height?: number | string;
  width?: number | string;
  className?: string;
};

// Default theme-aware options
const getDefaultOptions = (): ApexOptions => ({
  chart: {
    toolbar: {
      show: false,
    },
    fontFamily: 'inherit',
    background: 'transparent',
  },
  colors: [
    'var(--color-primary)',
    'var(--color-info)',
    'var(--color-success)',
    'var(--color-warning)',
    'var(--color-error)',
    'var(--color-secondary)',
  ],
  grid: {
    borderColor: 'rgb(229 231 235)', // gray-200
    strokeDashArray: 4,
  },
  xaxis: {
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
    labels: {
      style: {
        colors: 'rgb(107 114 128)', // gray-500
        fontSize: '12px',
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: 'rgb(107 114 128)', // gray-500
        fontSize: '12px',
      },
    },
  },
  tooltip: {
    theme: 'light',
  },
  legend: {
    labels: {
      colors: 'rgb(55 65 81)', // gray-700
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 2,
  },
  fill: {
    opacity: 1,
  },
});

const Chart = forwardRef<HTMLDivElement, ChartProps>(
  ({ type, series, options = {}, height = 350, width = '100%', className }, ref) => {
    const mergedOptions = useMemo(() => {
      const defaults = getDefaultOptions();
      return {
        ...defaults,
        ...options,
        chart: {
          ...defaults.chart,
          ...options.chart,
          type,
        },
        colors: options.colors || defaults.colors,
        grid: {
          ...defaults.grid,
          ...options.grid,
        },
        xaxis: {
          ...defaults.xaxis,
          ...options.xaxis,
        },
        yaxis: {
          ...defaults.yaxis,
          ...options.yaxis,
        },
        tooltip: {
          ...defaults.tooltip,
          ...options.tooltip,
        },
        legend: {
          ...defaults.legend,
          ...options.legend,
        },
      } as ApexOptions;
    }, [type, options]);

    return (
      <div ref={ref} className={className}>
        <ReactApexChart
          type={type}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          series={series as any}
          options={mergedOptions}
          height={height}
          width={width}
        />
      </div>
    );
  }
);

Chart.displayName = 'Chart';

export { Chart };
